import { useState, useEffect } from 'react';
import { fetchCMSContent } from '../lib/cms';

export default function GalleryPage() {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeImage, setActiveImage] = useState(null);

  useEffect(() => {
    async function loadGalleries() {
      const data = await fetchCMSContent('gallery');
      setGalleries(data || []);
      setLoading(false);
    }
    loadGalleries();
  }, []);

  // Saml alle billeder på tværs af gallerier med info om, hvilket galleri de tilhører
  const allImages = galleries.flatMap((g) => {
    const gSlug = typeof g.slug === 'object' ? g.slug.da : g.slug;
    return (g.images || []).map((imgUrl) => ({
      url: imgUrl,
      galleryTitle: g.title,
      gallerySlug: gSlug,
    }));
  });

  // Filtrer billeder baseret på det aktive filter
  const filteredImages = activeFilter === 'all'
    ? allImages
    : allImages.filter((img) => img.gallerySlug === activeFilter);

  const handlePrev = (e) => {
    e.stopPropagation();
    const currentIndex = filteredImages.findIndex((img) => img.url === activeImage);
    const prevIndex = currentIndex === 0 ? filteredImages.length - 1 : currentIndex - 1;
    setActiveImage(filteredImages[prevIndex].url);
  };

  const handleNext = (e) => {
    e.stopPropagation();
    const currentIndex = filteredImages.findIndex((img) => img.url === activeImage);
    const nextIndex = currentIndex === filteredImages.length - 1 ? 0 : currentIndex + 1;
    setActiveImage(filteredImages[nextIndex].url);
  };

  return (
    <>
      {/* React 19 Native Metadata Hoisting */}
      <title>Galleri - Nygaards Vinduespolering</title>
      <meta
        name="description"
        content="Se billeder fra Nygaards Vinduespolering og få et indtryk af arbejdet med vinduespolering og tagrende rens."
      />

      {/* Hero Header */}
      <section className="bg-slate-50/70 py-20 border-b border-slate-100">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-orange">
            Vores arbejde i billeder
          </span>
          <h1 className="mt-3 font-display text-3xl font-bold uppercase tracking-wider text-brand-blue sm:text-4xl">
            Galleri
          </h1>
          <div className="mx-auto mt-4 h-0.5 w-12 bg-brand-orange" />
          <p className="mx-auto mt-6 max-w-2xl text-sm font-light leading-relaxed text-slate-500">
            Se eksempler på vores arbejde med vinduespolering og tagrenderensning hos tilfredse kunder i Kerteminde og omegn.
          </p>
        </div>
      </section>

      {/* Galleri Sektion */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-blue border-t-transparent" />
            </div>
          ) : (
            <>
              {/* Filter Knapper */}
              <div className="flex flex-wrap justify-center gap-3 mb-12">
                <button
                  onClick={() => setActiveFilter('all')}
                  className={`rounded-full px-6 py-2.5 font-display text-xs font-bold uppercase tracking-widest transition-all ${
                    activeFilter === 'all'
                      ? 'bg-brand-blue text-white shadow-none'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  Vis alle
                </button>
                {galleries.map((g) => {
                  const gSlug = typeof g.slug === 'object' ? g.slug.da : g.slug;
                  return (
                    <button
                      key={gSlug}
                      onClick={() => setActiveFilter(gSlug)}
                      className={`rounded-full px-6 py-2.5 font-display text-xs font-bold uppercase tracking-widest transition-all ${
                        activeFilter === gSlug
                          ? 'bg-brand-blue text-white shadow-none'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {g.title}
                    </button>
                  );
                })}
              </div>

              {/* Billedgrid */}
              {filteredImages.length === 0 ? (
                <div className="text-center py-12 text-xs font-light text-slate-500">
                  Ingen billeder fundet i dette galleri.
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:gap-6">
                  {filteredImages.map((img, idx) => {
                    // Optimer billedet med Cloudflare on-the-fly resizing (600px bredde)
                    const optimizedUrl = img.url.includes('assets.auxodev.dk')
                      ? img.url.replace('assets.auxodev.dk/assets/', 'assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/')
                      : img.url;

                    return (
                      <button
                        key={idx}
                        onClick={() => setActiveImage(img.url)}
                        className="group relative aspect-4/3 overflow-hidden rounded-none bg-slate-100 shadow-none transition-all focus:outline-none focus:ring-1 focus:ring-brand-blue"
                      >
                        <img
                          src={optimizedUrl}
                          alt={`${img.galleryTitle} - Billede ${idx + 1}`}
                          className="h-full w-full object-cover rounded-none transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100 flex flex-col justify-end p-4 text-left">
                          <span className="text-[10px] font-bold uppercase tracking-widest text-brand-orange">
                            {img.galleryTitle}
                          </span>
                          <span className="mt-2 rounded-full bg-white/90 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-brand-dark flex items-center gap-1.5 shadow-none self-start">
                            <i className="fa-solid fa-magnifying-glass-plus text-brand-blue" />
                            Vis i stor
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Lightbox / Modal */}
      {activeImage !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4 backdrop-blur-sm transition-opacity"
          onClick={() => setActiveImage(null)}
        >
          {/* Luk-knap */}
          <button
            onClick={() => setActiveImage(null)}
            className="absolute top-4 right-4 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            aria-label="Luk"
          >
            <i className="fa-solid fa-xmark text-xl" />
          </button>

          {/* Forrige-knap */}
          <button
            onClick={handlePrev}
            className="absolute left-4 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            aria-label="Forrige"
          >
            <i className="fa-solid fa-chevron-left text-lg" />
          </button>

          {/* Billede */}
          <div className="relative max-h-[85vh] max-w-[90vw] overflow-hidden rounded-none" onClick={(e) => e.stopPropagation()}>
            <img
              src={activeImage}
              alt="Stor visning"
              className="max-h-[80vh] max-w-full object-contain rounded-none"
            />
            <div className="absolute bottom-0 inset-x-0 bg-black/60 px-4 py-3 text-center text-[10px] font-bold uppercase tracking-widest text-white/90">
              Billede {filteredImages.findIndex((img) => img.url === activeImage) + 1} af {filteredImages.length}
            </div>
          </div>

          {/* Næste-knap */}
          <button
            onClick={handleNext}
            className="absolute right-4 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            aria-label="Næste"
          >
            <i className="fa-solid fa-chevron-right text-lg" />
          </button>
        </div>
      )}
    </>
  );
}
